import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import classNames from 'classnames';
import { DateRangePicker as DateRangePickerBase } from 'react-dates';
import styles from './date_picker.css';

class DateRangePicker extends PureComponent {

    constructor(props) {
        super(props);
        this.state = {
            startDate: props.startDate,
            endDate: props.endDate,
            focusedInput: null
        };
        this.stateKey = 'vertical.quote';
    }

    /**
     * Fires the set_value action to store the start and end date
     */
    setStartEndDate = () => {
        const { identifier, dispatch } = this.props;
        const { startDate, endDate } = this.state;
        dispatch({
            type: 'SET_VALUE',
            value: startDate,
            identifier: `${identifier}.startDate`,
            stateKey: this.stateKey
        });
        dispatch({
            type: 'SET_VALUE',
            value: endDate,
            identifier: `${identifier}.endDate`,
            stateKey: this.stateKey
        });
    };

    /**
     * dispatches an action to store the difference value
     * @param  {Number} difference startDate - endDate difference
     */
    setDifference = (difference) => {
        const { identifier, dispatch } = this.props;

        dispatch({
            type: 'SET_VALUE',
            value: difference,
            identifier: `${identifier}.billDays`,
            stateKey: this.stateKey
        });
    };

    /**
     * checks validation and appends to the className
     * @return {String} returns className
     */
    getClassNames = () => {
        const { touched, valid } = this.props;
        let additionalClassName = '';
        if (touched) {
            const stateSuffix = valid ? '_valid' : '_invalid';
            additionalClassName = styles[`date_range_picker${stateSuffix}`];
        }
        return classNames(styles.date_range_picker_base, additionalClassName);
    };

    /**
     * Method to passdown to date_range_picker component to check if date is in range
     * @param {Moment} day Moment date
     * @return {Bool} returns if day is outside of range
     */
    isOutsideRange = (day) => {
        const { startDate, endDate } = this.state;
        const { maxDateRange, noFutureDates } = this.props;

        if (noFutureDates) {
            const today = moment.tz('Australia/Brisbane').startOf('day');
            if (today.diff(day.startOf('day')) < 0) {
                return true;
            }
        }
        if (maxDateRange) {
            const startDateDiff = Math.round(moment.duration(day.diff(startDate)).as('days'));
            const endDateDiff = Math.round(moment.duration(day.diff(endDate)).as('days'));
            return startDateDiff > maxDateRange || Math.abs(endDateDiff) > maxDateRange;
        }
        return false;
    };

    /**
     * Returns difference of startDate & endDate
     * @param {Moment} startDate Moment object start date
     * @param {Moment} endDate Moment object end date
     * @return {Number|Null} returns date difference or null
     */
    calcDifference = (startDate, endDate) => {
        // returns difference of startDate and endDate including the startDate instead of just the difference
        return startDate && endDate ? Math.round(moment.duration(endDate.diff(startDate)).as('days') + 1) : null;
    };

    /**
     * Handles date change functionality for date range picker
     * @param {Moment} startDate Moment object start date
     * @param {Moment} endDate Moment object end date
     */
    onDatesChange = ({ startDate, endDate }) => {
        const difference = this.calcDifference(startDate, endDate);
        this.setState({ startDate, endDate }, () => {
            if (difference) {
                this.setDifference(difference);
                this.setStartEndDate();
            }
        });
    };

    /**
     * Handles the onClose functionality for date range picker
     */
    onClose = () => {
        const { touched, dispatch, identifier } = this.props;
        if (!touched) {
            dispatch({
                type: 'ELEMENT_TOUCHED',
                identifier,
                stateKey: this.stateKey
            });
        }
        const { startDate, endDate } = this.state;
        if (startDate == null || endDate == null) {
            this.setDifference(null);
        }
    };

    /**
     * Handles focus changing
     * @param {String} focusedInput which input is currently focused
     */
    onFocusChange = (focusedInput) => {
        this.setState({ focusedInput });
    };

    /**
     * Wrapper component for the react dates date range picker
     * @returns {Node} Returns the date range picker
     */
    render() {
        const {
            displayFormat = 'DD/MM/YYYY',
            numberOfMonths,
            initialVisibleMonth
        } = this.props;
        const { startDate, endDate, focusedInput } = this.state;

        return (
            <div className={this.getClassNames()}>
                <DateRangePickerBase
                    initialVisibleMonth={initialVisibleMonth}
                    numberOfMonths={numberOfMonths}
                    onClose={this.onClose}
                    displayFormat={displayFormat}
                    isOutsideRange={this.isOutsideRange}
                    startDate={startDate}
                    endDate={endDate}
                    onDatesChange={this.onDatesChange}
                    onFocusChange={this.onFocusChange}
                    focusedInput={focusedInput}
                    hideKeyboardShortcutsPanel
                />
            </div>
        );
    }

}

DateRangePicker.propTypes = {
    touched: PropTypes.bool,
    valid: PropTypes.bool,
    identifier: PropTypes.string,
    dispatch: PropTypes.func,
    maxDateRange: PropTypes.number,
    displayFormat: PropTypes.string,
    numberOfMonths: PropTypes.number,
    initialVisibleMonth: PropTypes.func,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    noFutureDates: PropTypes.bool
};

export { DateRangePicker };
